import React, { useState, useEffect } from 'react';
import { Eye, Download, FileText, Calendar } from 'lucide-react';
import API_URL from '../../config';
import { useAuth } from '../../AuthContext';
import PdfViewerModal from './PdfViewerModal';

const EmployeePayslipView = () => {
  const [payslips, setPayslips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [viewState, setViewState] = useState({ isOpen: false, url: null, title: '' });
  const { logout } = useAuth();

  const fetchPayslips = React.useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/payslips/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPayslips(data);
      } else {
        if (response.status === 401) logout();
        setMessage({ text: 'Erreur lors du chargement des fiches de paie', type: 'error' });
      }
    } catch (error) {
      console.error('Error fetching payslips:', error);
      setMessage({ text: 'Erreur réseau', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    fetchPayslips();
  }, [fetchPayslips]);

  const handleView = async (payslipId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/payslips/${payslipId}/download`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setViewState({ 
          isOpen: true, 
          url: data.download_url, 
          title: `Fiche de paie` 
        });
      } else {
         setMessage({ text: 'Impossible de visualiser le fichier', type: 'error' });
      }
    } catch (error) {
      console.error('Error viewing payslip:', error);
      setMessage({ text: 'Erreur lors de la visualisation', type: 'error' });
    }
  };

  const handleDownload = async (payslipId, fileName) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/payslips/${payslipId}/download`, {
        headers: {
            'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const link = document.createElement('a');
        link.href = data.download_url;
        link.download = data.file_name || fileName;
        link.target = "_blank";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
         setMessage({ text: 'Impossible de télécharger le fichier', type: 'error' });
      }
    } catch (error) {
      console.error('Error downloading payslip:', error);
      setMessage({ text: 'Erreur lors du téléchargement', type: 'error' });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fr-FR', { month: 'long', year: 'numeric' }).format(date);
  };

  const formatShortDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(date);
  };

  if (loading) {
    return <div className="p-4 text-center">Chargement...</div>;
  }

  return (
    <div className="p-4 sm:p-0">
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-4 py-4 sm:px-6 border-b border-gray-200">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-800">Mes Fiches de Paie</h2>
          <p className="text-xs sm:text-sm text-gray-500 mt-1">Consultez et téléchargez vos fiches de paie</p>
        </div>
        
        {message.text && (
          <div className={`mx-4 mt-4 p-3 sm:p-4 rounded ${message.type === 'error' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'} text-sm`}>
            {message.text}
          </div>
        )}

        {payslips.length === 0 ? (
          <div className="p-8 sm:p-12 text-center">
            <FileText className="w-12 h-12 sm:w-16 sm:h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-sm sm:text-base">Aucune fiche de paie disponible.</p>
          </div>
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Période</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date de mise en ligne</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fichier</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {payslips.map((payslip) => (
                    <tr key={payslip.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {formatDate(payslip.period_start)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatShortDate(payslip.uploaded_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center">
                          <FileText className="w-4 h-4 mr-2 text-gray-400" />
                          <span className="truncate max-w-xs">{payslip.file_name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-3">
                          <button
                            onClick={() => handleView(payslip.id)}
                            className="text-indigo-600 hover:text-indigo-900"
                            title="Voir"
                          >
                            <Eye className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleDownload(payslip.id, payslip.file_name)}
                            className="text-gray-600 hover:text-gray-900"
                            title="Télécharger"
                          >
                            <Download className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="sm:hidden divide-y divide-gray-200">
              {payslips.map((payslip) => (
                <div key={payslip.id} className="p-4 hover:bg-gray-50 transition-colors">
                  {/* Period Header */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <Calendar className="w-5 h-5 text-indigo-600" />
                      <span className="text-sm font-semibold text-gray-900">
                        {formatDate(payslip.period_start)}
                      </span>
                    </div>
                  </div>

                  {/* File Info */}
                  <div className="space-y-2 mb-3">
                    <div className="flex items-start text-xs text-gray-600">
                      <FileText className="w-4 h-4 mr-2 mt-0.5 shrink-0 text-gray-400" />
                      <span className="truncate">{payslip.file_name}</span>
                    </div>
                    <div className="text-xs text-gray-500 pl-6">
                      Mis en ligne le {formatShortDate(payslip.uploaded_at)}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleView(payslip.id)}
                      className="flex-1 inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none"
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      Voir
                    </button>
                    <button
                      onClick={() => handleDownload(payslip.id, payslip.file_name)}
                      className="flex-1 inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Télécharger
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {viewState.isOpen && (
        <PdfViewerModal
          isOpen={viewState.isOpen}
          onClose={() => setViewState({ ...viewState, isOpen: false })}
          url={viewState.url}
          title={viewState.title}
        />
      )}
    </div>
  );
};

export default EmployeePayslipView;
